import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { CHIP_VARIANTS, type ChipTone } from './ChipRow'

/** A value the input can offer while typing, with an optional badge beside it. */
export interface TokenSuggestion {
  value: string
  /** Rendered on the right of the row, e.g. how often the value is seen. */
  badge?: React.ReactNode
}

export interface TokenInputProps {
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Lower-cases and trims every token as it is added. */
  lowercase?: boolean
  /** Rewrites a token as it is added, e.g. to fill in a URL's scheme. */
  normalize?: (token: string) => string
  /** Returns why a token cannot be added, or undefined when it is fine. */
  validate?: (token: string) => string | undefined
  /** Offers values while typing — run on the debounced input, empty term and all. */
  suggest?: (term: string) => Promise<TokenSuggestion[]>
  /** Query key prefix for the suggestions; the term is appended to it. */
  suggestQueryKey?: string
  /** Gives the field room for several lines, for lists that run long. */
  tall?: boolean
  /**
   * How the values read — the same tones the targeting summary uses, so a list
   * is the same colour wherever it is shown. Blue when left out.
   */
  tone?: ChipTone
}

/**
 * Free-text values as removable badges inside the field itself, the way the
 * label fields work: what has been added sits in the box, the caret after it.
 * Enter or a comma commits what has been typed, so a pasted `a, b, c` becomes
 * three values; Backspace on an empty input removes the last one. A `suggest`
 * callback offers what is already known in a dropdown, but never gates what
 * can be typed.
 */
export function TokenInput({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  lowercase,
  normalize,
  validate,
  suggest,
  suggestQueryKey = 'token-suggestions',
  tall,
  tone,
}: TokenInputProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string>()
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const debouncedDraft = useDebouncedValue(draft)

  const has = (token: string) => value.some((v) => v.toLowerCase() === token.toLowerCase())

  const { data: suggestions = [] } = useQuery({
    queryKey: [suggestQueryKey, debouncedDraft],
    queryFn: () => suggest!(debouncedDraft),
    enabled: !!suggest && open && !debouncedDraft.includes(','),
    staleTime: 60 * 1000,
  })
  const offered = suggestions.filter((suggestion) => !has(suggestion.value))

  // A click anywhere else closes the dropdown, so it does not hang over the
  // rest of the form once the field has been left.
  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  const commit = (text: string) => {
    const tokens = text
      .split(',')
      .map((token) => (lowercase ? token.trim().toLowerCase() : token.trim()))
      .filter(Boolean)
      .map((token) => normalize?.(token) ?? token)
    const rejected = tokens.filter((token) => validate?.(token))
    const accepted = tokens.filter((token) => !rejected.includes(token) && !has(token))

    if (accepted.length) onChange([...value, ...accepted])
    setError(rejected.length ? validate?.(rejected[0]) : undefined)
    // A single bad value is left in the input, normalised, so it can be
    // corrected rather than retyped.
    setDraft(rejected.length === 1 && tokens.length === 1 ? rejected[0] : '')
    setHighlight(-1)
  }

  const take = (suggestion: TokenSuggestion) => {
    if (!has(suggestion.value)) onChange([...value, suggestion.value])
    setDraft('')
    setHighlight(-1)
  }

  return (
    <div ref={containerRef} className={cn('relative flex flex-col gap-2', className)}>
      {/* The badges live in the field, with the caret after them. */}
      <div
        onClick={() => containerRef.current?.querySelector('input')?.focus()}
        className={cn(
          'flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1.5 text-sm shadow-xs transition-[color,box-shadow] dark:bg-input/30',
          tall && 'min-h-32 items-start content-start',
          'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
          disabled && 'pointer-events-none opacity-50'
        )}
      >
        {value.map((token) => (
          <Badge
            key={token}
            variant={tone ? CHIP_VARIANTS[tone] : 'blue'}
            className="gap-1 py-0.5 pr-1 pl-2.5 font-normal"
          >
            {tone === 'include' && <Check className="size-3" />}
            {tone === 'exclude' && <X className="size-3" />}
            {token}
            <button
              type="button"
              disabled={disabled}
              aria-label={t('common.remove')}
              className="rounded-sm opacity-60 hover:opacity-100"
              onClick={() => onChange(value.filter((v) => v !== token))}
            >
              <X className="size-3.5" />
            </button>
          </Badge>
        ))}
        <input
          value={draft}
          disabled={disabled}
          placeholder={value.length ? '' : (placeholder ?? t('targeting.addValuePlaceholder'))}
          className="min-w-32 flex-1 bg-transparent px-1 outline-none placeholder:text-muted-foreground"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            const text = e.target.value
            setError(undefined)
            setOpen(true)
            if (text.includes(',')) commit(text)
            else setDraft(text)
          }}
          onBlur={() => draft && commit(draft)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && offered.length) {
              e.preventDefault()
              setHighlight((current) => Math.min(current + 1, offered.length - 1))
            } else if (e.key === 'ArrowUp' && offered.length) {
              e.preventDefault()
              setHighlight((current) => Math.max(current - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              if (highlight >= 0 && offered[highlight]) take(offered[highlight])
              else commit(draft)
            } else if (e.key === 'Escape') {
              setOpen(false)
            } else if (e.key === 'Backspace' && !draft && value.length) {
              onChange(value.slice(0, -1))
            }
          }}
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}

      {/* What is already known, offered while the field is in use. The rows are
          taken on mousedown so the input's blur does not commit what was typed
          before the click lands. */}
      {open && offered.length > 0 && (
        <ul className="absolute top-full z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
          {offered.map((suggestion, index) => (
            <li key={suggestion.value}>
              <button
                type="button"
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent',
                  highlight === index && 'bg-accent'
                )}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => take(suggestion)}
              >
                <span className="truncate">{suggestion.value}</span>
                {suggestion.badge}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
